import { AddToBasketUI } from "./AddToBasketUI";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "../../utils/product/ensureIsActive";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { useEffect, useState } from "react";
import {
  ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
  ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
} from "../../constants/constants";
import { isAvailableToPreOrder } from "../../utils/variant/isAvailableToPreOrder";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { StandardProps } from "../componentMap";
import { ActiveProduct, Variant } from "../../types/types";
import { isFutureRelease } from "../../utils/variant/isFutureRelease";
import { getStoreStockSkuId } from "../../utils/variant/getStoreStockSkuId";
import { hasAnyStock } from "../../utils/product/hasAnyStock";
import { getVariantDimensions } from "../../utils/product/getVariantDimensions";
import { isPerMetre } from "../../utils/product/isPerMetre";
import { getMinLength } from "../../utils/variant/getMinLength";

export const SUPPLIER_DIRECT_DMS_ID = "14";

export function AddToBasket({
  configProps,
  product,
  runtimeConfig,
}: Pick<StandardProps, "product" | "runtimeConfig" | "configProps">) {
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const isPerMetreProduct = isPerMetre(product);
  const perMetreMinimumLength = isPerMetreProduct
    ? getMinLength(variant)
    : undefined;
  const recsPlacementId =
    typeof configProps?.recsPlacementId === "string"
      ? configProps?.recsPlacementId
      : "OSPStandardA2BClick";

  const oosPlacementId =
    typeof configProps?.oosPlacementId === "string"
      ? configProps.oosPlacementId
      : "OSPStandardOos";

  const showOosExperienceForMultiVariant =
    configProps?.showOosExperienceForMultiVariant === true;

  const [userAcknowledgementChecked, setUserAcknowledgementChecked] =
    useState<boolean>(false);

  const userAcknowledgementRequired =
    product?.termsAndConditions?.customerConfirmationRequired || false;

  useEffect(() => {
    const handleAddToBasketError = (event: Event) => {
      const { errors = [] } = (event as CustomEvent).detail;
      const hasVariantError = errors.some(
        ({ code }: { code: string }) => code === "no-variant",
      );

      if (hasVariantError) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
      } else {
        document.getElementById("product:basket:a2besi")?.scrollIntoView({
          behavior: "smooth",
          inline: "nearest",
        });
      }
    };
    window.addEventListener(
      ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
      handleAddToBasketError,
    );
    return () =>
      window.removeEventListener(
        ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
        handleAddToBasketError,
      );
  }, []);

  useEffect(() => {
    const handleUserAcknowledgement = (event: Event) => {
      const { userAcknowledgementChecked } = (event as CustomEvent).detail;
      setUserAcknowledgementChecked(userAcknowledgementChecked);
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
      handleUserAcknowledgement,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
        handleUserAcknowledgement,
      );
    };
  }, []);

  if (!activeProduct || !activeVariant) return null;

  const hasAvailableVariantsMatchingColour =
    checkHasAvailableVariantsMatchingColour(
      activeProduct.variants,
      activeVariant.differentiators?.colour,
    );

  const minimumAge = activeVariant.sellingRestrictions?.age?.minimumAge;

  const getPriceRange = () => {
    const display = product.variantPriceRange?.display;
    if (display === undefined) return "";

    return display.max === display.min
      ? display.max
      : `${display.min} – ${display.max}`;
  };

  return (
    <AddToBasketUI
      pimProductId={activeProduct.aliases.pimProductId}
      isSingleSkuProduct={product.variants.length === 1}
      storeStockDisplayCode={activeVariant.displayCode}
      storeStockSkuId={getStoreStockSkuId(activeVariant)}
      isFutureRelease={isFutureRelease(activeVariant)}
      isPreOrderable={isAvailableToPreOrder(activeVariant)}
      skuId={activeVariant.aliases.skuId}
      variantId={activeVariant.id}
      title={activeVariant.title}
      price={activeVariant.price?.value}
      customerNotifiableEvents={activeVariant.customerNotifiableEvents}
      isInStoreOnly={isInStoreOnly(activeVariant)}
      availableToOrder={activeVariant.availability.availableToOrder}
      availableToPreOrder={isAvailableToPreOrder(activeVariant)}
      supplierDirectMessage={getSupplierDirectMessage(activeProduct)}
      notificationApiBaseUrl={runtimeConfig.notificationApiBaseUrl}
      notificationApiKey={runtimeConfig.notificationApiKey}
      stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
      stockApiKey={runtimeConfig.stockApiKey}
      storeStockApiKey={runtimeConfig.storeStockApiKey}
      storeStockScriptUrl={runtimeConfig.storeStockScriptUrl}
      storeStockModalApiUrl={runtimeConfig.storeStockModalApiUrl}
      minimumAge={minimumAge}
      isSelected={isSelected}
      recsPlacementId={recsPlacementId}
      oosPlacementId={oosPlacementId}
      hasStock={hasAnyStock(product)}
      dimensions={getVariantDimensions(variant)}
      priceDisplay={isSelected ? variant.price : getPriceRange()}
      size={isSelected ? variant.differentiators?.size : undefined}
      hasAvailableVariantsMatchingColour={hasAvailableVariantsMatchingColour}
      colour={variant.differentiators?.colour}
      showOosExperienceForMultiVariant={showOosExperienceForMultiVariant}
      isPerMetre={isPerMetreProduct}
      perMetreMinimumLength={perMetreMinimumLength}
      userAcknowledgementChecked={userAcknowledgementChecked}
      userAcknowledgementConfirmationRequired={userAcknowledgementRequired}
    />
  );
}

function checkHasAvailableVariantsMatchingColour(
  variants: Variant[],
  colour: string | undefined | null,
) {
  return variants
    .filter((variant) => variant.differentiators?.colour === colour)
    .some((variant) => variant.availability?.availableToOrder);
}

function getSupplierDirectMessage(product: ActiveProduct) {
  const supplierDirectOption = product.fulfilmentOptions.available.find(
    (option) => option.aliases.dmsId === SUPPLIER_DIRECT_DMS_ID,
  );
  return supplierDirectOption?.shortDescription;
}
